import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';

import { cx } from './internal/cx.js';

export interface FormLabelProps extends ComponentPropsWithoutRef<'label'> {
  children: ReactNode;
  /** Marks the field as required for assistive tech. */
  required?: boolean;
}

/** Standalone label for grouped form controls. */
export function FormLabel({
  children,
  className,
  required,
  ...props
}: FormLabelProps): JSX.Element {
  return (
    <label className={cx('nrx-form-label', className)} {...props}>
      {children}
      {required ? (
        <span className="nrx-form-label__required" aria-hidden="true">
          {' '}
          *
        </span>
      ) : null}
    </label>
  );
}

export interface FormGroupProps extends ComponentPropsWithoutRef<'fieldset'> {
  children: ReactNode;
}

/** Groups related inputs with optional legend via FormLabel. */
export function FormGroup({ children, className, ...props }: FormGroupProps): JSX.Element {
  return (
    <fieldset className={cx('nrx-form-group', className)} {...props}>
      {children}
    </fieldset>
  );
}

export interface FormHelperTextProps extends ComponentPropsWithoutRef<'p'> {
  children: ReactNode;
  /** Renders as error styling when true. */
  error?: boolean;
}

/** Supplementary or validation text below a form control. */
export function FormHelperText({
  children,
  className,
  error = false,
  ...props
}: FormHelperTextProps): JSX.Element {
  return (
    <p className={cx('nrx-form-helper', error && 'nrx-form-helper--error', className)} {...props}>
      {children}
    </p>
  );
}
